import * as React from 'react';
import { useContext } from 'react';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import type { EnterpriseProgramFromSlugFragment } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
import type { EnterpriseNoticeAcceptanceLogsV1 } from 'bundles/naptimejs/resources/__generated__/EnterpriseNoticeAcceptanceLogsV1';
import type { ProgramBrowsingExperiencesV1 } from 'bundles/naptimejs/resources/__generated__/ProgramBrowsingExperiencesV1';
import type ProgramBrowsingExperienceV1 from 'bundles/naptimejs/resources/programBrowsingExperiences.v1';
import type { ProgramMembershipType } from 'bundles/program-home/types/ProgramMembershipAdapter';
import type {
  GetEnterpriseContractFragment,
  GetEnterpriseContractTagFragment,
} from 'bundles/unified-common/queries/__generated__/EnterpriseUserRostersByProgramId';

export type EnterpriseProgramContextType = {
  programBrowsingExperience?: ProgramBrowsingExperiencesV1;
  programContractTags?: GetEnterpriseContractTagFragment[];
  enterpriseContracts?: GetEnterpriseContractFragment[];
  isC4er?: boolean;
  isC4wr?: boolean;
  isWESContract?: boolean;
  isC4Program?: boolean;
  requirePreJoinCheck?: boolean;
  browsingExperienceType?: ProgramBrowsingExperienceV1;
  program?: EnterpriseProgramFromSlugFragment;
  isProgramLoading?: boolean;
  programMembership?: ProgramMembershipType;
  isC4cv?: boolean;
  isUpswell?: boolean;
  isC4C?: boolean;
  isProgramMember?: boolean;
  membershipCreatedAt?: number | null;
  isMembershipLoading?: boolean;
  isProgramAdmin?: boolean;
  isInvited?: boolean;
  userEmails?: UserEmail[];
  refetchUserEmails?: () => Promise<unknown> | unknown;
  hasFreemiumTag?: boolean;
  isGwGProgram?: boolean;
  showNoCertificateNotification?: boolean;
  isWildcard?: boolean;
  acceptanceNoticeLogs?: EnterpriseNoticeAcceptanceLogsV1[];
  recoveryEmail?: {
    emailAddress: string;
    userId: string;
    isVerified: boolean;
  } | null;
  isRecoveryEmailLoading?: boolean;
  reloadProgramMembership?: () => void | Promise<unknown>;
};

const EnterpriseProgramContext = React.createContext<EnterpriseProgramContextType | undefined>(undefined);

export const useProgramContractTags = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.programContractTags;
};
export const useEnterpriseContracts = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.enterpriseContracts;
};
export const useIsWESContract = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.isWESContract;
};
export const useRequirePreJoinCheck = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.requirePreJoinCheck;
};
export const useBrowsingExperienceType = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.browsingExperienceType;
};

export const useProgram = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.program;
};

export const useIsProgramLoading = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.isProgramLoading;
};

export const useIsC4cv = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.isC4cv;
};

export const useIsC4C = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.isC4C;
};

export const useIsUpswell = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.isUpswell;
};

export const useUserEmails = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return { userEmails: programContext?.userEmails, refetchUserEmails: programContext?.refetchUserEmails };
};

export const useRecoveryEmail = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return {
    recoveryEmail: programContext?.recoveryEmail,
    isRecoveryEmailLoading: programContext?.isRecoveryEmailLoading,
  };
};

export const useIsGwGProgram = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.isGwGProgram;
};

export type UseProgramMembershipReturn = {
  membershipCreatedAt?: number | null;
  membership?: ProgramMembershipType;
  isProgramMember?: boolean;
  isProgramAdmin?: boolean;
  isInvited?: boolean;
  isEmailNotVerified?: boolean;
  isMembershipLoading?: boolean;
  reloadProgramMembership?: () => void | Promise<unknown>;
};

export const useProgramMembership = (): UseProgramMembershipReturn => {
  const programContext = useContext(EnterpriseProgramContext);
  const reloadProgramMembership = programContext?.reloadProgramMembership;
  return {
    membershipCreatedAt: programContext?.membershipCreatedAt,
    membership: programContext?.programMembership,
    isProgramMember: programContext?.isProgramMember,
    isProgramAdmin: programContext?.isProgramAdmin,
    isInvited: programContext?.isInvited,
    isEmailNotVerified: programContext?.programMembership?.isEmailNotVerified,
    isMembershipLoading: programContext?.isMembershipLoading,
    ...(reloadProgramMembership != null ? { reloadProgramMembership } : {}),
  };
};

export const useIsWildcard = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.isWildcard;
};

export const useAcceptanceNoticeLogs = () => {
  const programContext = useContext(EnterpriseProgramContext);

  return programContext?.acceptanceNoticeLogs;
};

export default EnterpriseProgramContext;
